import { useState, useCallback } from "react";
import ConfirmDialog from "../components/ConfirmDialog/ConfirmDialog.js";

/**
 * Hook para substituir window.confirm() e window.prompt() por modais elegantes.
 *
 * Retorna:
 *   - confirm(opts): abre diálogo de confirmação e devolve Promise<boolean>
 *   - prompt(opts): abre diálogo com input e devolve Promise<string|null>
 *   - confirmDialog: JSX pronto para colocar no return
 *
 * Uso:
 *   const { confirm, confirmDialog } = useConfirm();
 *   if (!await confirm({ title: "...", message: "...", variant: "danger" })) return;
 *   ...
 *   return (<>{confirmDialog}</>);
 */
export default function useConfirm() {
  const [promptOpts, setPromptOpts] = useState(null);

  const confirm = useCallback((opts) => {
    return new Promise((resolve) => {
      setPromptOpts({
        ...opts,
        withInput: false,
        _resolve: resolve,
      });
    });
  }, []);

  const prompt = useCallback((opts) => {
    return new Promise((resolve) => {
      setPromptOpts({
        ...opts,
        withInput: true,
        _resolve: resolve,
      });
    });
  }, []);

  const handleConfirm = useCallback((value) => {
    setPromptOpts((prev) => {
      if (prev && prev._resolve) prev._resolve(value);
      return null;
    });
  }, []);

  const handleCancel = useCallback(() => {
    setPromptOpts((prev) => {
      if (prev && prev._resolve) prev._resolve(prev.withInput ? null : false);
      return null;
    });
  }, []);

  const confirmDialog = promptOpts ? (
    <ConfirmDialog
      title={promptOpts.title}
      message={promptOpts.message}
      withInput={promptOpts.withInput}
      inputPlaceholder={promptOpts.inputPlaceholder}
      inputDefaultValue={promptOpts.inputDefaultValue}
      confirmText={promptOpts.confirmText || "Confirmar"}
      cancelText={promptOpts.cancelText || "Cancelar"}
      variant={promptOpts.variant || "default"}
      onConfirm={handleConfirm}
      onCancel={handleCancel}
    />
  ) : null;

  const closeDialog = useCallback(() => {
    setPromptOpts((prev) => {
      if (prev && prev._resolve) prev._resolve(prev.withInput ? null : false);
      return null;
    });
  }, []);

  return {
    confirm,
    prompt,
    confirmDialog,
    closeDialog,
  };
}
